import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const SUPPORTED_LANGS = ['en', 'zh-Hant', 'zh-CN'];

const LANGUAGE_STORAGE_KEY = 'ai-vet-language';

const normalizeLanguage = (value) => {
  if (!value) return 'en';
  const normalized = String(value).trim();
  if (normalized === 'zh-TW' || normalized === 'zh-HK') return 'zh-Hant';
  if (SUPPORTED_LANGS.includes(normalized)) return normalized;
  return 'en';
};

const interpolate = (template, params = {}) =>
  String(template).replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? ''));

const translations = {
  en: {
    loading: 'Loading...',
    loading_pets: 'Loading pets...',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    page: 'Page',

    nav_dashboard: 'Dashboard',
    nav_pets: 'Pets',
    nav_assessment: 'Assessment',
    nav_chat: 'AI Chat',
    nav_settings: 'Profile',
    nav_sign_out: 'Sign out',
    nav_sign_in: 'Sign in',
    nav_get_started: 'Get started',

    home_sign_in: 'Sign in',
    home_get_started: 'Get started',
    home_title_ai: 'Smarter care for every',
    home_title_vet: 'pet concern',
    home_badge: 'AI Veterinary Assistant',
    home_hero_desc: 'Photo-based screening, guided questioning, and clear next steps for pet owners.',
    home_cta_primary: 'Start free assessment',
    home_stat_response: 'Response time',
    home_stat_availability: 'Availability',
    home_consult_flow: 'Consult flow',
    home_consult_flow_desc: 'Upload photo -> Describe symptoms -> AI triage -> Suggested next action',
    home_features_title: 'Built for real pet care decisions',
    home_features_desc: 'A practical workflow from concern to recommendation.',
    home_feature_image_title: 'Image Analysis',
    home_feature_image_desc: 'Upload pet photos for visual triage and risk signals.',
    home_feature_chat_title: 'AI Chat',
    home_feature_chat_desc: 'Conversational veterinary guidance with per-pet context.',
    home_feature_reports_title: 'Care Reports',
    home_feature_reports_desc: 'Structured summaries, recommendations, and follow-up priorities.',
    home_feature_multi_title: 'Multi-Pet',
    home_feature_multi_desc: 'Manage multiple pets with dedicated health timelines.',
    home_feature_smart_title: 'Smart Suggestions',
    home_feature_smart_desc: 'Personalized guidance from symptoms, age, and breed context.',
    home_feature_always_title: 'Always Available',
    home_feature_always_desc: '24/7 first-line support before clinic escalation.',

    login_title: 'Sign in to AI Vet',
    login_or_create: 'Or',
    login_create_new_account: 'create a new account',
    login_username: 'Username',
    login_enter_username: 'Enter your username',
    login_password: 'Password',
    login_enter_password: 'Enter your password',
    login_remember_me: 'Remember me',
    login_forgot_password: 'Forgot password?',
    login_signing_in: 'Signing in...',
    login_button: 'Sign in',
    login_new_to: 'New to AI Vet?',
    login_create_account_cta: 'Create account',

    register_title: 'Create your account',
    register_already_have: 'Already have an account?',
    register_signin_here: 'Sign in here',
    register_first_name: 'First name',
    register_first_name_ph: 'First name',
    register_last_name: 'Last name',
    register_last_name_ph: 'Last name',
    register_username: 'Username',
    register_username_ph: 'Choose a username',
    register_email: 'Email',
    register_email_ph: 'you@example.com',
    register_password: 'Password',
    register_password_ph: 'Create a password',
    register_password_hint: 'Use at least 8 characters.',
    register_confirm_password: 'Confirm password',
    register_confirm_password_ph: 'Enter password again',
    register_terms_prefix: 'I agree to the',
    register_terms: 'Terms',
    register_and: 'and',
    register_privacy: 'Privacy Policy',
    register_creating: 'Creating account...',
    register_button: 'Create account',
    register_signin_account: 'Sign in to existing account',
    register_success_title: 'Account created',
    register_success_desc: 'Redirecting to your dashboard...',
    register_err_password_mismatch: 'Passwords do not match.',
    register_err_password_short: 'Password must be at least 8 characters.',
    register_err_failed: 'Registration failed. Please try again.',

    forgot_title: 'Forgot password',
    forgot_subtitle: 'Enter your email and we will send a reset link.',
    forgot_email: 'Email',
    forgot_send_link: 'Send reset link',
    forgot_sending: 'Sending...',
    forgot_back_login: 'Back to sign in',
    forgot_success_title: 'Check your inbox',
    forgot_success_desc: 'If the email exists, a password reset link has been sent.',
    forgot_err_failed: 'Unable to send reset link.',

    reset_title: 'Reset password',
    reset_subtitle: 'Choose a new password for your account.',
    reset_new_password: 'New password',
    reset_new_password_ph: 'Enter new password',
    reset_confirm_password: 'Confirm password',
    reset_confirm_password_ph: 'Enter new password again',
    reset_submit: 'Reset password',
    reset_submitting: 'Resetting...',
    reset_success_title: 'Password updated',
    reset_success_desc: 'You can now sign in with your new password.',
    reset_err_mismatch: 'Passwords do not match.',
    reset_err_short: 'Password must be at least 8 characters.',
    reset_err_failed: 'Unable to reset password.',

    dash_loading: 'Loading dashboard...',
    dash_loading_assess: 'Loading recent assessments...',
    dash_welcome: 'Welcome back',
    dash_subtitle: 'Track your pets, assessments, and recent care activity.',
    dash_total_pets: 'Total pets',
    dash_total_assessments: 'Assessments',
    dash_health_score: 'Health score',
    dash_this_month: 'This month',
    dash_not_available: 'N/A',
    dash_quick_actions: 'Quick actions',
    dash_recent_assess: 'Recent assessments',
    dash_view_all: 'View all',
    dash_unknown_pet: 'Unknown pet',
    dash_health_check: 'Health check',
    dash_health_score_label: 'Health score',
    assess_risk_level: 'Risk level',
    assess_risk_level_hint: 'This indicates how urgent the case appears based on the current chat and uploaded evidence.',
    assess_confidence: 'Confidence',
    assess_confidence_hint: 'This reflects how confident the AI is in this assessment, not how healthy the pet is.',
    risk_level_low: 'Low',
    risk_level_medium: 'Medium',
    risk_level_high: 'High',
    risk_level_critical: 'Critical',
    dash_no_assess: 'No assessments yet',
    dash_start_first_assessment: 'Start your first assessment to generate AI care guidance.',
    dash_start_assess: 'Start assessment',
    dash_your_pets: 'Your pets',
    dash_manage_pets: 'Manage pets',
    dash_view_all_pets: 'View all {count} pets',
    dash_no_pets: 'No pets added yet',
    dash_add_first_pet: 'Add first pet',
    dash_recent_error: 'Failed to load recent assessments.',

    pets_title: 'Pet records',
    pets_subtitle: 'Manage your pets and their baseline health information.',
    add_pet: 'Add pet',
    add_new_pet: 'Add new pet',
    edit_pet: 'Edit pet',
    pets_pet_name: 'Pet name',
    pets_placeholder_pet_name: 'Enter pet name',
    pets_species: 'Species',
    pets_select_species: 'Select species',
    pets_species_dog: 'Dog',
    pets_species_cat: 'Cat',
    pets_species_bird: 'Bird',
    pets_species_rabbit: 'Rabbit',
    pets_species_hamster: 'Hamster',
    pets_species_other: 'Other',
    pets_breed: 'Breed',
    pets_placeholder_breed: 'Breed',
    pets_age: 'Age',
    pets_age_years: 'Age (years)',
    pets_placeholder_age: 'Age',
    pets_weight_lbs: 'Weight',
    pets_placeholder_weight: 'Weight',
    pets_color: 'Color',
    pets_placeholder_color: 'Color',
    pets_medical_history: 'Medical history',
    pets_placeholder_medical_history: 'Past illnesses, surgeries, medications, or notes',
    pets_allergies: 'Allergies',
    pets_placeholder_allergies: 'Known allergies',
    pets_vet_name: 'Veterinarian name',
    pets_placeholder_vet_name: 'Veterinarian name',
    pets_vet_phone: 'Vet phone',
    pets_placeholder_vet_phone: 'Vet phone number',
    pets_emergency_contact: 'Emergency contact',
    pets_placeholder_emergency_contact: 'Emergency contact',
    pets_saving: 'Saving...',
    pets_update_pet: 'Update pet',
    pets_save_failed: 'Failed to save pet information.',
    pets_update_failed: 'Failed to update pet.',
    pets_add_failed: 'Failed to add pet.',
    pets_update_success: 'Pet updated successfully.',
    pets_add_success: 'Pet added successfully.',
    pets_delete_confirm: 'Delete this pet? This action cannot be undone.',
    pets_delete_success: 'Pet deleted successfully.',
    pets_delete_failed: 'Failed to delete pet.',
    pets_vaccine_notice_title: 'Reminder rules',
    pets_vaccine_notice_over_24h: 'If the appointment is more than 24 hours away, we will remind you one day before.',
    pets_vaccine_notice_within_24h: 'If the appointment is within 24 hours, we will send a confirmation reminder immediately.',

    assess_title: 'Health assessment',
    assess_edit: 'Review assessment',
    assess_subtitle_new: 'Upload pet photos, chat with AI, and generate a care report.',
    assess_subtitle_edit: 'Continue reviewing the existing assessment.',
    assess_step_select: 'Select pet',
    assess_step_select_desc: 'Choose the pet for this assessment.',
    assess_step_upload: 'Upload images',
    assess_step_upload_desc: 'Add photos for visual review.',
    assess_step_chat: 'Chat with AI',
    assess_step_chat_desc: 'Explain symptoms and concerns.',
    assess_step_review: 'Review report',
    assess_step_review_desc: 'See summary and recommendations.',
    assess_select_pet: 'Select a pet',
    assess_no_pets_found: 'No pets found',
    assess_need_pet_first: 'Add a pet profile before starting an assessment.',
    assess_add_first_pet: 'Add first pet',
    assess_continue_to_upload: 'Continue to upload',
    assess_upload_photos: 'Upload photos',
    assess_upload_area_title: 'Upload pet photos',
    assess_upload_area_hint: 'Click to add one or more images.',
    assess_uploaded_images: 'Uploaded images',
    assess_consult: 'Consult',
    assess_analyzing_images: 'Uploading and analyzing images...',
    assess_image_analysis: 'Image analysis summary',
    assess_possible_issues: 'Potential issues detected',
    assess_no_obvious_concerns: 'No obvious concerns detected',
    assess_back: 'Back',
    assess_continue: 'Continue',
    assess_select_pet_before_continue: 'Select a pet before continuing.',
    assess_upload_required_before_continue: 'Upload at least one photo before continuing.',
    assess_select_consult_image_before_continue: 'Select at least one uploaded photo for consultation before continuing.',
    assess_chat_ai: 'Chat with AI',
    assess_image_consultation: 'Image consultation',
    assess_ask_image_placeholder: 'Ask a question about the selected image',
    assess_select_image_first: 'Select an image first',
    assess_select_images_first: 'Select at least one image first',
    assess_selected_images: '{count} image(s) selected',
    assess_ask_with_image: 'Ask with image',
    assess_chat_start: 'Start the conversation',
    assess_chat_start_hint: 'Describe what you observe, symptoms, or recent changes.',
    assess_message_placeholder: 'Type your message here...',
    assess_generate: 'Generate report',
    assess_report: 'Assessment report',
    assess_generating_report: 'Generating report...',
    assess_generating_report_hint: 'The AI is summarizing findings and next steps.',
    assess_pet_info: 'Pet information',
    assess_summary: 'Assessment summary',
    assess_recommendations: 'Recommendations',
    assess_follow_up: 'Follow-up',
    assess_download: 'Download',
    assess_print: 'Print',
    assess_new: 'New assessment',
    assess_progress: 'Assessment progress',
    assess_pet_selected: 'Pet selected',
    assess_not_selected: 'Not selected',
    assess_images_uploaded: 'Images uploaded',
    assess_photos_count: '{count} photo(s)',
    assess_chat_messages: 'Chat messages',
    assess_messages_count: '{count} message(s)',
    assess_report_status: 'Report status',
    assess_generated: 'Generated',
    assess_pending: 'Pending',
    assess_load_failed: 'Failed to load assessment. Please try again.',
    assess_select_pet_required: 'Select a pet before starting an assessment.',
    assess_upload_failed: 'Failed to upload or analyze image(s). Please try again.',
    assess_image_limit: 'Maximum {count} photos allowed.',
    assess_upload_count: '{count}/{max} photos uploaded',
    assess_upload_limit_reached: 'Upload limit reached',
    assess_upload_multi_hint: 'You can upload up to {count} photos. They will be evaluated together.',
    assess_chat_error: 'I am having trouble processing your message right now. Please try again.',
    assess_image_done: 'Image consultation completed.',
    assess_image_failed: 'Image consultation failed. Please try again.',
    assess_report_error: 'Failed to generate report. Please try again.',
    assess_report_default_1: 'Continue monitoring your pet\'s behavior.',
    assess_report_default_2: 'Ensure regular hydration, exercise, and proper nutrition.',
    assess_report_default_3: 'Schedule a follow-up if symptoms persist or worsen.',
    assess_follow_up_recommended: 'Follow-up recommended',
    assess_follow_up_routine: 'Routine monitoring',
    assess_report_generated: 'Report generated.',
    assess_report_file: 'health-report',
    assess_print_title: 'Health assessment report',

    chat_title: 'AI pet consultation',
    chat_subtitle: 'Ask questions, upload a pet photo, or add medical documents.',
    chat_typing: 'AI is thinking...',
    chat_new: 'New chat',
    chat_select_pet_optional: 'Select pet',
    chat_no_pets: 'No pets available yet.',
    chat_clear_pet_memory: 'Clear pet memory',
    chat_delete_pet_chat_history: 'Delete chat history',
    chat_delete_pet_documents: 'Delete medical documents',
    chat_pet_memory_cleared: 'Pet memory cleared.',
    chat_pet_history_deleted: 'Chat history deleted.',
    chat_pet_docs_deleted: 'Medical documents deleted.',
    chat_upload_notice: 'You can upload pet photos or medical documents to get more accurate guidance.',
    chat_quick_actions: 'Suggested questions',
    chat_start_conversation: 'Start a conversation with AI Vet',
    chat_thinking: 'Thinking...',
    chat_recent_docs: 'Recently uploaded documents',
    chat_input_placeholder: 'Describe your pet\'s condition or ask a question...',
    chat_upload: 'Upload photo',
    chat_medical_doc: 'Medical document',
    chat_upload_document: 'Upload',
    chat_uploading: 'Uploading...',
    chat_sending: 'Sending...',
    chat_send: 'Send',
    chat_auth_required: 'Authentication required. Please sign in again.',
    chat_select_pet_photo: 'Select a pet first to use photo consultation.',
    chat_select_pet_document: 'Select a pet first to upload medical documents.',
    chat_select_pet_before_upload: 'Select a pet before uploading medical documents.',
    chat_upload_document_failed: 'Failed to upload medical document. Allowed formats: PDF, JPG, PNG.',
    chat_async_failed: 'Async task failed.',
    chat_async_timeout: 'Async task timed out.',
    chat_default_reply: 'I understand your concern. Could you provide more details?',
    chat_request_cancelled: 'Request cancelled.',
    chat_timeout_error: 'Request timed out. The AI is taking longer than expected to respond.',
    chat_timeout_reply: 'I am taking longer than usual to process your request. Please try again shortly.',
    chat_auth_error: 'Authentication failed. Please sign in again.',
    chat_auth_reply: 'Please refresh the page and sign in again to continue.',
    chat_server_error: 'Server error. Please try again later.',
    chat_server_reply: 'I am experiencing technical difficulties. Please try again shortly.',
    chat_send_failed: 'Failed to send message. Please try again.',
    chat_action_failed: 'Action failed. Please try again.',
    chat_select_pet_first: 'Select a pet first.',
    chat_quick_1: 'My pet has low appetite today. What should I monitor first?',
    chat_quick_2: 'Is this behavior change normal or a warning sign?',
    chat_quick_3: 'When should I go to an emergency vet immediately?',
    chat_quick_4: 'Please review this symptom with my pet history in mind.',
    chat_photo_consultation_enabled: 'Photo consultation enabled',

    profile_title: 'Profile',
    profile_subtitle: 'Manage your account details.',
    profile_username: 'Username',
    profile_not_logged_in: 'Not signed in',
    profile_email: 'Email',
    profile_enter_new_email: 'Enter new email',
    profile_saving: 'Saving...',
    profile_no_email: 'No email on file',
    profile_member_since: 'Member since',
    profile_account_status: 'Account status',
    profile_active: 'Active',
    profile_dashboard: 'Back to dashboard',
    profile_sign_out: 'Sign out',
    profile_invalid_email: 'Please enter a valid email address.',
    profile_email_updated: 'Email updated successfully.',
    profile_email_update_failed: 'Unable to update email.',
  },
  'zh-Hant': {
    loading: '載入中...',
    loading_pets: '正在載入寵物資料...',
    save: '儲存',
    cancel: '取消',
    back: '上一頁',
    next: '下一頁',
    page: '第',

    nav_dashboard: '總覽',
    nav_pets: '寵物',
    nav_assessment: '健康評估',
    nav_chat: 'AI 諮詢',
    nav_settings: '個人資料',
    nav_sign_out: '登出',
    nav_sign_in: '登入',
    nav_get_started: '開始使用',

    home_sign_in: '登入',
    home_get_started: '開始使用',
    home_title_ai: '更聰明地處理每一個',
    home_title_vet: '寵物健康疑問',
    home_badge: 'AI 獸醫助理',
    home_hero_desc: '提供照片判讀、引導式問診與清晰下一步建議，協助主人更快作出判斷。',
    home_cta_primary: '開始免費評估',
    home_stat_response: '回應時間',
    home_stat_availability: '服務時間',
    home_consult_flow: '諮詢流程',
    home_consult_flow_desc: '上傳照片 -> 描述症狀 -> AI 分流 -> 建議下一步',
    home_features_title: '為真實寵物照護決策而設',
    home_features_desc: '從擔心到建議，一個清晰可用的實戰流程。',
    home_feature_image_title: '圖片分析',
    home_feature_image_desc: '上傳寵物照片，快速取得視覺分流與風險提示。',
    home_feature_chat_title: 'AI 對話',
    home_feature_chat_desc: '結合寵物背景資料的多輪獸醫諮詢。',
    home_feature_reports_title: '照護報告',
    home_feature_reports_desc: '結構化整理重點、建議與後續追蹤優先次序。',
    home_feature_multi_title: '多寵管理',
    home_feature_multi_desc: '多隻寵物可獨立保存健康紀錄與互動脈絡。',
    home_feature_smart_title: '智能建議',
    home_feature_smart_desc: '按症狀、年齡與品種提供更貼近情況的建議。',
    home_feature_always_title: '隨時可用',
    home_feature_always_desc: '24/7 提供第一線支援，協助判斷是否需要盡快就醫。',

    login_title: '登入 AI Vet',
    login_or_create: '或',
    login_create_new_account: '建立新帳戶',
    login_username: '用戶名稱',
    login_enter_username: '請輸入用戶名稱',
    login_password: '密碼',
    login_enter_password: '請輸入密碼',
    login_remember_me: '記住我',
    login_forgot_password: '忘記密碼？',
    login_signing_in: '登入中...',
    login_button: '登入',
    login_new_to: '第一次使用 AI Vet？',
    login_create_account_cta: '建立帳戶',

    register_title: '建立帳戶',
    register_already_have: '已有帳戶？',
    register_signin_here: '立即登入',
    register_first_name: '名字',
    register_first_name_ph: '名字',
    register_last_name: '姓氏',
    register_last_name_ph: '姓氏',
    register_username: '用戶名稱',
    register_username_ph: '請選擇用戶名稱',
    register_email: '電郵',
    register_email_ph: 'you@example.com',
    register_password: '密碼',
    register_password_ph: '建立密碼',
    register_password_hint: '至少需要 8 個字元。',
    register_confirm_password: '確認密碼',
    register_confirm_password_ph: '再次輸入密碼',
    register_terms_prefix: '我同意',
    register_terms: '服務條款',
    register_and: '及',
    register_privacy: '私隱政策',
    register_creating: '建立中...',
    register_button: '建立帳戶',
    register_signin_account: '登入現有帳戶',
    register_success_title: '帳戶建立成功',
    register_success_desc: '即將跳轉至總覽頁...',
    register_err_password_mismatch: '兩次輸入的密碼不一致。',
    register_err_password_short: '密碼至少需要 8 個字元。',
    register_err_failed: '註冊失敗，請稍後再試。',

    forgot_title: '忘記密碼',
    forgot_subtitle: '輸入你的電郵，我們會寄送重設連結。',
    forgot_email: '電郵',
    forgot_send_link: '寄送重設連結',
    forgot_sending: '寄送中...',
    forgot_back_login: '返回登入',
    forgot_success_title: '請檢查電郵',
    forgot_success_desc: '若該電郵存在，系統已寄出重設密碼連結。',
    forgot_err_failed: '無法寄送重設連結。',

    reset_title: '重設密碼',
    reset_subtitle: '為你的帳戶設定新密碼。',
    reset_new_password: '新密碼',
    reset_new_password_ph: '輸入新密碼',
    reset_confirm_password: '確認密碼',
    reset_confirm_password_ph: '再次輸入新密碼',
    reset_submit: '重設密碼',
    reset_submitting: '重設中...',
    reset_success_title: '密碼已更新',
    reset_success_desc: '你現在可以使用新密碼登入。',
    reset_err_mismatch: '兩次輸入的密碼不一致。',
    reset_err_short: '密碼至少需要 8 個字元。',
    reset_err_failed: '無法重設密碼。',

    dash_loading: '正在載入總覽...',
    dash_loading_assess: '正在載入最近評估...',
    dash_welcome: '歡迎回來',
    dash_subtitle: '查看寵物、評估與最近照護活動。',
    dash_total_pets: '寵物數量',
    dash_total_assessments: '評估次數',
    dash_health_score: '健康分數',
    dash_this_month: '本月',
    dash_quick_actions: '快速操作',
    dash_recent_assess: '最近評估',
    dash_view_all: '查看全部',
    dash_unknown_pet: '未知寵物',
    dash_health_check: '健康檢查',
    dash_health_score_label: '健康分數',
    dash_no_assess: '尚未有評估紀錄',
    dash_start_first_assessment: '開始第一次評估，取得 AI 照護建議。',
    dash_start_assess: '開始評估',
    dash_your_pets: '你的寵物',
    dash_manage_pets: '管理寵物',
    dash_view_all_pets: '查看全部 {count} 隻寵物',
    dash_no_pets: '尚未新增寵物',
    dash_add_first_pet: '新增第一隻寵物',
    dash_recent_error: '無法載入最近評估。',

    pets_title: '寵物檔案',
    pets_subtitle: '管理寵物資料與基礎健康資訊。',
    add_pet: '新增寵物',
    add_new_pet: '新增寵物',
    edit_pet: '編輯寵物',
    pets_pet_name: '寵物名稱',
    pets_placeholder_pet_name: '請輸入寵物名稱',
    pets_species: '種類',
    pets_select_species: '請選擇種類',
    pets_species_dog: '狗',
    pets_species_cat: '貓',
    pets_species_bird: '鳥',
    pets_species_rabbit: '兔',
    pets_species_hamster: '倉鼠',
    pets_species_other: '其他',
    pets_breed: '品種',
    pets_placeholder_breed: '請輸入品種',
    pets_age: '年齡',
    pets_age_years: '年齡（歲）',
    pets_placeholder_age: '請輸入年齡',
    pets_weight_lbs: '體重',
    pets_placeholder_weight: '請輸入體重',
    pets_color: '顏色',
    pets_placeholder_color: '請輸入顏色',
    pets_medical_history: '病史',
    pets_placeholder_medical_history: '過往疾病、手術、藥物或其他備註',
    pets_allergies: '過敏史',
    pets_placeholder_allergies: '已知過敏反應',
    pets_vet_name: '獸醫姓名',
    pets_placeholder_vet_name: '請輸入獸醫姓名',
    pets_vet_phone: '獸醫電話',
    pets_placeholder_vet_phone: '請輸入獸醫電話',
    pets_emergency_contact: '緊急聯絡人',
    pets_placeholder_emergency_contact: '請輸入緊急聯絡人',
    pets_saving: '儲存中...',
    pets_update_pet: '更新寵物資料',
    pets_save_failed: '儲存寵物資料失敗。',
    pets_update_failed: '更新寵物資料失敗。',
    pets_add_failed: '新增寵物失敗。',
    pets_update_success: '寵物資料已更新。',
    pets_add_success: '已新增寵物。',
    pets_delete_confirm: '確定刪除此寵物？此操作無法復原。',
    pets_delete_success: '已刪除寵物。',
    pets_delete_failed: '刪除寵物失敗。',
    pets_vaccine_notice_title: '提醒規則',
    pets_vaccine_notice_over_24h: '如果預約時間距離現在超過 24 小時，系統會在前一日提醒。',
    pets_vaccine_notice_within_24h: '如果預約時間在 24 小時內，系統會在建立預約後立即發送確認提醒。',

    assess_title: '健康評估',
    assess_edit: '查看評估',
    assess_subtitle_new: '上傳照片、與 AI 對話，並生成照護報告。',
    assess_subtitle_edit: '繼續查看現有評估內容。',
    assess_step_select: '選擇寵物',
    assess_step_select_desc: '選擇本次評估的寵物。',
    assess_step_upload: '上傳圖片',
    assess_step_upload_desc: '加入照片以作視覺分析。',
    assess_step_chat: '與 AI 對話',
    assess_step_chat_desc: '描述症狀與擔心事項。',
    assess_step_review: '查看報告',
    assess_step_review_desc: '閱讀摘要與建議。',
    assess_select_pet: '選擇寵物',
    assess_no_pets_found: '找不到寵物資料',
    assess_need_pet_first: '開始評估前，請先新增寵物檔案。',
    assess_add_first_pet: '新增第一隻寵物',
    assess_continue_to_upload: '前往上傳',
    assess_upload_photos: '上傳照片',
    assess_upload_area_title: '上傳寵物照片',
    assess_upload_area_hint: '點擊以上傳一張或多張圖片。',
    assess_uploaded_images: '已上傳圖片',
    assess_consult: '諮詢此圖',
    assess_analyzing_images: '正在上傳及分析圖片...',
    assess_image_analysis: '圖片分析摘要',
    assess_possible_issues: '偵測到可能問題',
    assess_no_obvious_concerns: '暫未發現明顯異常',
    assess_back: '返回',
    assess_continue: '繼續',
    assess_select_pet_before_continue: '請先選擇寵物再繼續。',
    assess_upload_required_before_continue: '請先上傳至少一張照片，才可繼續。',
    assess_select_consult_image_before_continue: '請先選擇至少一張已上傳照片作諮詢，才可繼續。',
    assess_chat_ai: '與 AI 對話',
    assess_image_consultation: '圖片諮詢',
    assess_ask_image_placeholder: '輸入關於所選圖片的問題',
    assess_select_image_first: '請先選擇圖片',
    assess_select_images_first: '請先選擇至少一張圖片',
    assess_selected_images: '已選擇 {count} 張圖片',
    assess_ask_with_image: '連同圖片提問',
    assess_chat_start: '開始對話',
    assess_chat_start_hint: '描述你觀察到的情況、症狀或近期變化。',
    assess_message_placeholder: '輸入你的訊息...',
    assess_generate: '生成報告',
    assess_report: '評估報告',
    assess_generating_report: '正在生成報告...',
    assess_generating_report_hint: 'AI 正在整理重點與下一步建議。',
    assess_pet_info: '寵物資料',
    assess_summary: '評估摘要',
    assess_recommendations: '建議事項',
    assess_follow_up: '後續追蹤',
    assess_download: '下載',
    assess_print: '列印',
    assess_new: '新評估',
    assess_progress: '評估進度',
    assess_pet_selected: '已選擇寵物',
    assess_not_selected: '尚未選擇',
    assess_images_uploaded: '已上傳圖片',
    assess_photos_count: '{count} 張照片',
    assess_chat_messages: '對話訊息',
    assess_messages_count: '{count} 則訊息',
    assess_report_status: '報告狀態',
    assess_generated: '已生成',
    assess_pending: '待生成',
    assess_load_failed: '載入評估失敗，請稍後再試。',
    assess_select_pet_required: '開始評估前請先選擇寵物。',
    assess_upload_failed: '上傳或分析圖片失敗，請稍後再試。',
    assess_image_limit: '最多只能上傳 {count} 張照片。',
    assess_upload_count: '已上傳 {count}/{max} 張照片',
    assess_upload_limit_reached: '已達上傳上限',
    assess_upload_multi_hint: '最多可上傳 {count} 張照片，系統會一併用作本次評估。',
    assess_chat_error: '暫時無法處理你的訊息，請稍後再試。',
    assess_image_done: '圖片諮詢已完成。',
    assess_image_failed: '圖片諮詢失敗，請稍後再試。',
    assess_report_error: '生成報告失敗，請稍後再試。',
    assess_report_default_1: '持續觀察寵物的行為變化。',
    assess_report_default_2: '確保有足夠飲水、運動與均衡營養。',
    assess_report_default_3: '若症狀持續或惡化，請安排覆診。',
    assess_follow_up_recommended: '建議進一步跟進',
    assess_follow_up_routine: '持續例行觀察',
    assess_report_generated: '報告已生成。',
    assess_report_file: 'health-report',
    assess_print_title: '健康評估報告',

    chat_title: 'AI 寵物諮詢',
    chat_subtitle: '可提問、上傳寵物照片或補充病歷文件。',
    chat_typing: 'AI 思考中...',
    chat_new: '新對話',
    chat_select_pet_optional: '選擇寵物',
    chat_no_pets: '目前尚未有寵物資料。',
    chat_clear_pet_memory: '清除寵物記憶',
    chat_delete_pet_chat_history: '刪除聊天紀錄',
    chat_delete_pet_documents: '刪除病歷文件',
    chat_pet_memory_cleared: '已清除寵物記憶。',
    chat_pet_history_deleted: '已刪除聊天紀錄。',
    chat_pet_docs_deleted: '已刪除病歷文件。',
    chat_upload_notice: '可上傳寵物照片或病歷文件，讓 AI 提供更準確的建議。',
    chat_quick_actions: '建議問題',
    chat_start_conversation: '開始與 AI Vet 對話',
    chat_thinking: '思考中...',
    chat_recent_docs: '最近上傳的文件',
    chat_input_placeholder: '描述寵物狀況或直接提問...',
    chat_upload: '上傳照片',
    chat_medical_doc: '病歷文件',
    chat_upload_document: '上傳',
    chat_uploading: '上傳中...',
    chat_sending: '傳送中...',
    chat_send: '送出',
    chat_auth_required: '需要重新登入後才可繼續。',
    chat_select_pet_photo: '使用圖片諮詢前，請先選擇寵物。',
    chat_select_pet_document: '上傳病歷文件前，請先選擇寵物。',
    chat_select_pet_before_upload: '請先選擇寵物，再上傳病歷文件。',
    chat_upload_document_failed: '病歷文件上傳失敗。支援格式：PDF、JPG、PNG。',
    chat_async_failed: '非同步任務失敗。',
    chat_async_timeout: '非同步任務逾時。',
    chat_default_reply: '我明白你的擔心，可以再提供更多細節嗎？',
    chat_request_cancelled: '請求已取消。',
    chat_timeout_error: '請求逾時，AI 回應時間較平常長。',
    chat_timeout_reply: '我需要較多時間處理你的請求，請稍後再試。',
    chat_auth_error: '驗證失敗，請重新登入。',
    chat_auth_reply: '請重新整理頁面並再次登入，以繼續對話。',
    chat_server_error: '伺服器錯誤，請稍後再試。',
    chat_server_reply: '系統暫時出現技術問題，請稍後再試。',
    chat_send_failed: '訊息傳送失敗，請稍後再試。',
    chat_action_failed: '操作失敗，請稍後再試。',
    chat_select_pet_first: '請先選擇寵物。',
    chat_quick_1: '我家寵物今天食慾差，應先觀察什麼？',
    chat_quick_2: '這種行為改變屬正常還是警號？',
    chat_quick_3: '什麼情況下應立即去急診獸醫？',
    chat_quick_4: '請結合我寵物的病史，分析這個症狀。',
    chat_photo_consultation_enabled: '已啟用照片諮詢',

    profile_title: '個人資料',
    profile_subtitle: '管理你的帳戶資訊。',
    profile_username: '用戶名稱',
    profile_not_logged_in: '尚未登入',
    profile_email: '電郵',
    profile_enter_new_email: '輸入新電郵',
    profile_saving: '儲存中...',
    profile_no_email: '未設定電郵',
    profile_member_since: '加入時間',
    profile_account_status: '帳戶狀態',
    profile_active: '正常',
    profile_dashboard: '返回總覽',
    profile_sign_out: '登出',
    profile_invalid_email: '請輸入有效的電郵地址。',
    profile_email_updated: '電郵已成功更新。',
    profile_email_update_failed: '無法更新電郵。',
  },
  'zh-CN': {
    loading: '加载中...',
    loading_pets: '正在加载宠物资料...',
    save: '保存',
    cancel: '取消',
    back: '上一页',
    next: '下一页',
    page: '第',

    nav_dashboard: '总览',
    nav_pets: '宠物',
    nav_assessment: '健康评估',
    nav_chat: 'AI 咨询',
    nav_settings: '个人资料',
    nav_sign_out: '退出登录',
    nav_sign_in: '登录',
    nav_get_started: '开始使用',

    home_sign_in: '登录',
    home_get_started: '开始使用',
    home_title_ai: '更聪明地处理每一个',
    home_title_vet: '宠物健康疑问',
    home_badge: 'AI 兽医助理',
    home_hero_desc: '提供照片判读、引导式问诊与清晰下一步建议，帮助主人更快作出判断。',
    home_cta_primary: '开始免费评估',
    home_stat_response: '响应时间',
    home_stat_availability: '服务时间',
    home_consult_flow: '咨询流程',
    home_consult_flow_desc: '上传照片 -> 描述症状 -> AI 分诊 -> 建议下一步',
    home_features_title: '为真实宠物照护决策而设',
    home_features_desc: '从担心到建议，一个清晰实用的流程。',
    home_feature_image_title: '图片分析',
    home_feature_image_desc: '上传宠物照片，快速获得视觉分诊与风险提示。',
    home_feature_chat_title: 'AI 对话',
    home_feature_chat_desc: '结合宠物背景资料的多轮兽医咨询。',
    home_feature_reports_title: '照护报告',
    home_feature_reports_desc: '结构化整理重点、建议与后续追踪优先级。',
    home_feature_multi_title: '多宠管理',
    home_feature_multi_desc: '多只宠物可独立保存健康记录与互动上下文。',
    home_feature_smart_title: '智能建议',
    home_feature_smart_desc: '按症状、年龄与品种提供更贴近情况的建议。',
    home_feature_always_title: '随时可用',
    home_feature_always_desc: '24/7 提供第一线支持，帮助判断是否需要尽快就医。',

    login_title: '登录 AI Vet',
    login_or_create: '或',
    login_create_new_account: '创建新账户',
    login_username: '用户名',
    login_enter_username: '请输入用户名',
    login_password: '密码',
    login_enter_password: '请输入密码',
    login_remember_me: '记住我',
    login_forgot_password: '忘记密码？',
    login_signing_in: '登录中...',
    login_button: '登录',
    login_new_to: '第一次使用 AI Vet？',
    login_create_account_cta: '创建账户',

    register_title: '创建账户',
    register_already_have: '已有账户？',
    register_signin_here: '立即登录',
    register_first_name: '名字',
    register_first_name_ph: '名字',
    register_last_name: '姓氏',
    register_last_name_ph: '姓氏',
    register_username: '用户名',
    register_username_ph: '请选择用户名',
    register_email: '邮箱',
    register_email_ph: 'you@example.com',
    register_password: '密码',
    register_password_ph: '创建密码',
    register_password_hint: '至少需要 8 个字符。',
    register_confirm_password: '确认密码',
    register_confirm_password_ph: '再次输入密码',
    register_terms_prefix: '我同意',
    register_terms: '服务条款',
    register_and: '和',
    register_privacy: '隐私政策',
    register_creating: '创建中...',
    register_button: '创建账户',
    register_signin_account: '登录现有账户',
    register_success_title: '账户创建成功',
    register_success_desc: '即将跳转到总览页...',
    register_err_password_mismatch: '两次输入的密码不一致。',
    register_err_password_short: '密码至少需要 8 个字符。',
    register_err_failed: '注册失败，请稍后再试。',

    forgot_title: '忘记密码',
    forgot_subtitle: '输入你的邮箱，我们会发送重置链接。',
    forgot_email: '邮箱',
    forgot_send_link: '发送重置链接',
    forgot_sending: '发送中...',
    forgot_back_login: '返回登录',
    forgot_success_title: '请检查邮箱',
    forgot_success_desc: '如果该邮箱存在，系统已发送重置密码链接。',
    forgot_err_failed: '无法发送重置链接。',

    reset_title: '重置密码',
    reset_subtitle: '为你的账户设置新密码。',
    reset_new_password: '新密码',
    reset_new_password_ph: '输入新密码',
    reset_confirm_password: '确认密码',
    reset_confirm_password_ph: '再次输入新密码',
    reset_submit: '重置密码',
    reset_submitting: '重置中...',
    reset_success_title: '密码已更新',
    reset_success_desc: '你现在可以使用新密码登录。',
    reset_err_mismatch: '两次输入的密码不一致。',
    reset_err_short: '密码至少需要 8 个字符。',
    reset_err_failed: '无法重置密码。',

    dash_loading: '正在加载总览...',
    dash_loading_assess: '正在加载最近评估...',
    dash_welcome: '欢迎回来',
    dash_subtitle: '查看宠物、评估与最近照护活动。',
    dash_total_pets: '宠物数量',
    dash_total_assessments: '评估次数',
    dash_health_score: '健康分数',
    dash_this_month: '本月',
    dash_quick_actions: '快捷操作',
    dash_recent_assess: '最近评估',
    dash_view_all: '查看全部',
    dash_unknown_pet: '未知宠物',
    dash_health_check: '健康检查',
    dash_health_score_label: '健康分数',
    dash_no_assess: '还没有评估记录',
    dash_start_first_assessment: '开始第一次评估，获取 AI 照护建议。',
    dash_start_assess: '开始评估',
    dash_your_pets: '你的宠物',
    dash_manage_pets: '管理宠物',
    dash_view_all_pets: '查看全部 {count} 只宠物',
    dash_no_pets: '还没有添加宠物',
    dash_add_first_pet: '添加第一只宠物',
    dash_recent_error: '无法加载最近评估。',

    pets_title: '宠物档案',
    pets_subtitle: '管理宠物资料与基础健康信息。',
    add_pet: '添加宠物',
    add_new_pet: '添加新宠物',
    edit_pet: '编辑宠物',
    pets_pet_name: '宠物名称',
    pets_placeholder_pet_name: '请输入宠物名称',
    pets_species: '种类',
    pets_select_species: '请选择种类',
    pets_species_dog: '狗',
    pets_species_cat: '猫',
    pets_species_bird: '鸟',
    pets_species_rabbit: '兔',
    pets_species_hamster: '仓鼠',
    pets_species_other: '其他',
    pets_breed: '品种',
    pets_placeholder_breed: '请输入品种',
    pets_age: '年龄',
    pets_age_years: '年龄（岁）',
    pets_placeholder_age: '请输入年龄',
    pets_weight_lbs: '体重',
    pets_placeholder_weight: '请输入体重',
    pets_color: '颜色',
    pets_placeholder_color: '请输入颜色',
    pets_medical_history: '病史',
    pets_placeholder_medical_history: '过往疾病、手术、药物或其他备注',
    pets_allergies: '过敏史',
    pets_placeholder_allergies: '已知过敏反应',
    pets_vet_name: '兽医姓名',
    pets_placeholder_vet_name: '请输入兽医姓名',
    pets_vet_phone: '兽医电话',
    pets_placeholder_vet_phone: '请输入兽医电话',
    pets_emergency_contact: '紧急联系人',
    pets_placeholder_emergency_contact: '请输入紧急联系人',
    pets_saving: '保存中...',
    pets_update_pet: '更新宠物资料',
    pets_save_failed: '保存宠物资料失败。',
    pets_update_failed: '更新宠物资料失败。',
    pets_add_failed: '添加宠物失败。',
    pets_update_success: '宠物资料已更新。',
    pets_add_success: '已添加宠物。',
    pets_delete_confirm: '确定删除该宠物？此操作无法撤销。',
    pets_delete_success: '已删除宠物。',
    pets_delete_failed: '删除宠物失败。',
    pets_vaccine_notice_title: '提醒规则',
    pets_vaccine_notice_over_24h: '如果预约时间距离现在超过 24 小时，系统会在前一日提醒。',
    pets_vaccine_notice_within_24h: '如果预约时间在 24 小时内，系统会在创建预约后立即发送确认提醒。',

    assess_title: '健康评估',
    assess_edit: '查看评估',
    assess_subtitle_new: '上传照片、与 AI 对话，并生成照护报告。',
    assess_subtitle_edit: '继续查看现有评估内容。',
    assess_step_select: '选择宠物',
    assess_step_select_desc: '选择本次评估的宠物。',
    assess_step_upload: '上传图片',
    assess_step_upload_desc: '添加照片用于视觉分析。',
    assess_step_chat: '与 AI 对话',
    assess_step_chat_desc: '描述症状与担心事项。',
    assess_step_review: '查看报告',
    assess_step_review_desc: '阅读摘要与建议。',
    assess_select_pet: '选择宠物',
    assess_no_pets_found: '找不到宠物资料',
    assess_need_pet_first: '开始评估前，请先添加宠物档案。',
    assess_add_first_pet: '添加第一只宠物',
    assess_continue_to_upload: '前往上传',
    assess_upload_photos: '上传照片',
    assess_upload_area_title: '上传宠物照片',
    assess_upload_area_hint: '点击上传一张或多张图片。',
    assess_uploaded_images: '已上传图片',
    assess_consult: '咨询此图',
    assess_analyzing_images: '正在上传并分析图片...',
    assess_image_analysis: '图片分析摘要',
    assess_possible_issues: '检测到可能问题',
    assess_no_obvious_concerns: '暂未发现明显异常',
    assess_back: '返回',
    assess_continue: '继续',
    assess_select_pet_before_continue: '请先选择宠物再继续。',
    assess_upload_required_before_continue: '请先上传至少一张照片，才可继续。',
    assess_select_consult_image_before_continue: '请先选择至少一张已上传照片作咨询，才可继续。',
    assess_chat_ai: '与 AI 对话',
    assess_image_consultation: '图片咨询',
    assess_ask_image_placeholder: '输入关于所选图片的问题',
    assess_select_image_first: '请先选择图片',
    assess_select_images_first: '请先选择至少一张图片',
    assess_selected_images: '已选择 {count} 张图片',
    assess_ask_with_image: '结合图片提问',
    assess_chat_start: '开始对话',
    assess_chat_start_hint: '描述你观察到的情况、症状或近期变化。',
    assess_message_placeholder: '输入你的消息...',
    assess_generate: '生成报告',
    assess_report: '评估报告',
    assess_generating_report: '正在生成报告...',
    assess_generating_report_hint: 'AI 正在整理重点与下一步建议。',
    assess_pet_info: '宠物资料',
    assess_summary: '评估摘要',
    assess_recommendations: '建议事项',
    assess_follow_up: '后续跟进',
    assess_download: '下载',
    assess_print: '打印',
    assess_new: '新评估',
    assess_progress: '评估进度',
    assess_pet_selected: '已选择宠物',
    assess_not_selected: '尚未选择',
    assess_images_uploaded: '已上传图片',
    assess_photos_count: '{count} 张照片',
    assess_chat_messages: '对话消息',
    assess_messages_count: '{count} 条消息',
    assess_report_status: '报告状态',
    assess_generated: '已生成',
    assess_pending: '待生成',
    assess_load_failed: '加载评估失败，请稍后再试。',
    assess_select_pet_required: '开始评估前请先选择宠物。',
    assess_upload_failed: '上传或分析图片失败，请稍后再试。',
    assess_image_limit: '最多只能上传 {count} 张照片。',
    assess_upload_count: '已上传 {count}/{max} 张照片',
    assess_upload_limit_reached: '已达上传上限',
    assess_upload_multi_hint: '最多可上传 {count} 张照片，系统会一并用于本次评估。',
    assess_chat_error: '暂时无法处理你的消息，请稍后再试。',
    assess_image_done: '图片咨询已完成。',
    assess_image_failed: '图片咨询失败，请稍后再试。',
    assess_report_error: '生成报告失败，请稍后再试。',
    assess_report_default_1: '持续观察宠物的行为变化。',
    assess_report_default_2: '确保有足够饮水、运动与均衡营养。',
    assess_report_default_3: '若症状持续或恶化，请安排复诊。',
    assess_follow_up_recommended: '建议进一步跟进',
    assess_follow_up_routine: '持续例行观察',
    assess_report_generated: '报告已生成。',
    assess_report_file: 'health-report',
    assess_print_title: '健康评估报告',

    chat_title: 'AI 宠物咨询',
    chat_subtitle: '可提问、上传宠物照片或补充病历文件。',
    chat_typing: 'AI 思考中...',
    chat_new: '新对话',
    chat_select_pet_optional: '选择宠物',
    chat_no_pets: '目前还没有宠物资料。',
    chat_clear_pet_memory: '清除宠物记忆',
    chat_delete_pet_chat_history: '删除聊天记录',
    chat_delete_pet_documents: '删除病历文件',
    chat_pet_memory_cleared: '已清除宠物记忆。',
    chat_pet_history_deleted: '已删除聊天记录。',
    chat_pet_docs_deleted: '已删除病历文件。',
    chat_upload_notice: '可上传宠物照片或病历文件，让 AI 提供更准确的建议。',
    chat_quick_actions: '建议问题',
    chat_start_conversation: '开始与 AI Vet 对话',
    chat_thinking: '思考中...',
    chat_recent_docs: '最近上传的文件',
    chat_input_placeholder: '描述宠物状况或直接提问...',
    chat_upload: '上传照片',
    chat_medical_doc: '病历文件',
    chat_upload_document: '上传',
    chat_uploading: '上传中...',
    chat_sending: '发送中...',
    chat_send: '发送',
    chat_auth_required: '需要重新登录后才可继续。',
    chat_select_pet_photo: '使用图片咨询前，请先选择宠物。',
    chat_select_pet_document: '上传病历文件前，请先选择宠物。',
    chat_select_pet_before_upload: '请先选择宠物，再上传病历文件。',
    chat_upload_document_failed: '病历文件上传失败。支持格式：PDF、JPG、PNG。',
    chat_async_failed: '异步任务失败。',
    chat_async_timeout: '异步任务超时。',
    chat_default_reply: '我理解你的担心，可以再提供更多细节吗？',
    chat_request_cancelled: '请求已取消。',
    chat_timeout_error: '请求超时，AI 响应时间比平时更长。',
    chat_timeout_reply: '我需要更多时间处理你的请求，请稍后再试。',
    chat_auth_error: '验证失败，请重新登录。',
    chat_auth_reply: '请刷新页面并重新登录后继续对话。',
    chat_server_error: '服务器错误，请稍后再试。',
    chat_server_reply: '系统暂时出现技术问题，请稍后再试。',
    chat_send_failed: '消息发送失败，请稍后再试。',
    chat_action_failed: '操作失败，请稍后再试。',
    chat_select_pet_first: '请先选择宠物。',
    chat_quick_1: '我家宠物今天食欲差，应该先观察什么？',
    chat_quick_2: '这种行为变化算正常还是警讯？',
    chat_quick_3: '什么情况下应该立刻去急诊兽医？',
    chat_quick_4: '请结合我宠物的病史，分析这个症状。',
    chat_photo_consultation_enabled: '已启用照片咨询',

    profile_title: '个人资料',
    profile_subtitle: '管理你的账户信息。',
    profile_username: '用户名',
    profile_not_logged_in: '尚未登录',
    profile_email: '邮箱',
    profile_enter_new_email: '输入新邮箱',
    profile_saving: '保存中...',
    profile_no_email: '未设置邮箱',
    profile_member_since: '加入时间',
    profile_account_status: '账户状态',
    profile_active: '正常',
    profile_dashboard: '返回总览',
    profile_sign_out: '退出登录',
    profile_invalid_email: '请输入有效的邮箱地址。',
    profile_email_updated: '邮箱已成功更新。',
    profile_email_update_failed: '无法更新邮箱。',
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY)));

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (value) => setLanguageState(normalizeLanguage(value));

  const t = useMemo(() => {
    return (key, params) => {
      const langTable = translations[language] || translations.en;
      const fallback = translations.en[key];
      const text = langTable[key] ?? fallback ?? key;
      return interpolate(text, params);
    };
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t
  }), [language, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
